import React, { useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { es, fr } from 'date-fns/locale';

import styled, { useTheme } from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import { Modal } from 'lib/components';
import { Button } from 'react-covideo-common';

import 'react-datepicker/dist/react-datepicker.css';
import { IoMdCalendar, IoMdClose } from 'react-icons/io';

// Register date-fns locales for react-datepicker
registerLocale('es', es);
registerLocale('fr', fr);

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.black[100]};
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.white[100]};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 500px;
  display: flex;
  flex-direction: column;
  margin-top: 40px;
`;

const InputDatesWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.neutral[60]};
`;

const InputDateWrapper = styled.div`
  position: relative;
  input {
    padding: 8px 8px 8px 40px;
    border-radius: 4px;
    max-width: 177px;
    border: solid 1px ${({ theme }) => theme.colors.neutral[40]};
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: ${({ theme }) => theme.colors.black[100]};
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  color: ${({ theme }) => theme.colors.neutral[60]};
  top: 50%;
  left: 8px;
  z-index: 1;
  height: 18px;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
`;

const DatePickerWrapper = styled.div`
  width: 100%;
  height: 260px;
  margin-top: 20px;
  & .react-datepicker {
    width: 100%;
  }
  & .react-datepicker__month-container {
    width: 50%;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

type Props = {
  initStartDate: Date;
  initEndDate: Date;
  minDate?: Date;
  onRangeSelected: (startDate: Date | null, endDate: Date | null) => void;
  handleModalClose: () => void;
};

export const ModalDateRangeSelector = (props: Props) => {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const {
    initStartDate,
    initEndDate,
    handleModalClose,
    onRangeSelected,
    minDate,
  } = props;

  const [startDate, setStartDate] = useState<Date | null>(initStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initEndDate);
  const { ref } = useOnClickOutside(handleModalClose);

  // Get the current locale for react-datepicker
  const getDatePickerLocale = () => {
    const currentLang = i18n.language;
    if (currentLang.startsWith('es')) return 'es';
    if (currentLang.startsWith('fr')) return 'fr';
    return undefined; // English is the default
  };

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };
  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  const onDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const onStartDateChange = (date: Date | null) => {
    if (date) {
      setStartDate(date);
    }
  };

  const onEndDateChange = (date: Date | null) => {
    if (date) {
      setEndDate(date);
    }
  };

  return (
    <Modal>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>{t('Custom Date Range')}</HeaderTitle>
          <CloseButtonWrap title={t('Close')}>
            <IoMdClose
              size={24}
              onClick={handleModalClose}
              color={colors.neutral[60]}
            />
          </CloseButtonWrap>
        </Header>
        <Content>
          <InputDatesWrapper>
            <InputDateWrapper>
              <IconWrapper>
                <IoMdCalendar size={18} />
              </IconWrapper>
              <DatePicker
                onChange={onStartDateChange}
                selected={startDate}
                open={false}
                minDate={minDate}
                maxDate={new Date()}
                locale={getDatePickerLocale()}
              />
            </InputDateWrapper>

            <div style={{ margin: '0 12px' }}>-</div>
            <InputDateWrapper>
              <IconWrapper>
                <IoMdCalendar size={18} />
              </IconWrapper>
              <DatePicker
                onChange={onEndDateChange}
                selected={endDate}
                open={false}
                minDate={minDate}
                maxDate={new Date()}
                locale={getDatePickerLocale()}
              />
            </InputDateWrapper>
          </InputDatesWrapper>
          <DatePickerWrapper>
            <DatePicker
              selected={startDate}
              onChange={onDateChange}
              startDate={startDate}
              endDate={endDate}
              monthsShown={2}
              minDate={minDate}
              maxDate={new Date()}
              selectsRange
              inline
              locale={getDatePickerLocale()}
            />
          </DatePickerWrapper>
        </Content>
        <Footer>
          <Button
            text={t('Done')}
            onClick={() => onRangeSelected(startDate, endDate)}
          />
        </Footer>
      </div>
    </Modal>
  );
};
